/**
 * plazaRoom — klient Colyseus dla sceny placu handlowego (Etap 5.B.4a + 4b-2).
 *
 * Cienki wrapper na `colyseus.js` (0.15) izolujący PlazaScene od szczegółów
 * sieci. Scena dostaje callbacki (add/remove/change/reset/status) + `sendMove`
 * + `leave`.
 *
 * AUTH: cookie `capibara_session` (httpOnly, host-only `localhost`) leci
 * automatycznie w WS handshake — port nieistotny dla matchowania cookie, więc
 * działa cross-port (5173 → 2567). Serwer waliduje w bramce `/matchmake` (5.B.1)
 * i ponownie w `onAuth`. Bez ważnego cookie `joinOrCreate` rzuca → caller łapie.
 *
 * STAN: NIE importujemy klas Schema z serwera — colyseus.js odtwarza definicję
 * stanu z handshake'u (reflection). Opisujemy tylko lokalny "widok" (pola, które
 * czytamy) + metody callbacków ze schema 2.x (`onAdd`/`onRemove`/`onChange`
 * zwracają funkcję deregistrującą).
 *
 * RECONNECT: najpierw wracamy `reconnectionToken` do TEJ SAMEJ sesji (30 s po
 * stronie serwera), więc krótki drop nie zrywa handlu. Po wygaśnięciu tokenu
 * robimy świeży join i próbujemy dalej z capped backoffem.
 *
 * Server-authoritative: klient TYLKO czyta cudze pozycje i wysyła swoją (`move`).
 * Nie ufamy niczemu lokalnie — serwer clampuje pozycje do granic planszy.
 */

import { Client, type Room } from 'colyseus.js';
import type { PlazaCommsPresetId } from '@/config/plazaCommunication.config';

const REALTIME_URL = (import.meta.env.VITE_REALTIME_URL ?? 'ws://localhost:2567').replace(/\/+$/, '');
const ROOM_NAME = 'plaza';

// Reconnect: backoff 0.5→8 s (cap). Rezerwacja tej samej sesji na serwerze = 30 s.
const RECONNECT_BASE_MS = 500;
const RECONNECT_CAP_MS = 8000;
const SESSION_RESUME_WINDOW_MS = 30_000;
// Kody zamknięcia, po których NIE wznawiamy: 1000 = normalne WS close,
// 4000 = Colyseus `CloseCode.CONSENTED` (klient świadomie wyszedł / kick).
// Restart serwera używa 4001, więc trafia w ścieżkę reconnect → fresh join.
const NO_RECONNECT_CLOSE_CODES = new Set([1000, 4000]);
// 4212 = poprzedni pokój nie istnieje (typowo restart procesu). 4214 może
// oznaczać, że serwer jeszcze nie zauważył zerwania — wtedy nadal próbujemy token.
const RESUME_IMPOSSIBLE_ERROR_CODES = new Set([4212]);

export type PlazaStatus = 'connected' | 'reconnecting' | 'disconnected';

/** Equipped pet innego gracza, widoczny w plazie (read-only snapshot z DB). */
export interface RemoteEquippedPetView {
  readonly uid: string;
  readonly defId: string;
  readonly variant: string;
}

/** Pola gracza, które renderuje PlazaScene (mirror PlayerState z serwera). */
export interface RemotePlayerView {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarSkin: string;
  readonly isGuest: boolean;
  readonly x: number;
  readonly y: number;
  readonly dir: string;
  readonly equippedPets: readonly RemoteEquippedPetView[];
}

export type PlazaStallChangeReason =
  | 'rent'
  | 'list_pet'
  | 'withdraw_pet'
  | 'list_eggs'
  | 'withdraw_eggs'
  | 'buy_pet'
  | 'buy_eggs'
  | 'extend'
  | 'expire'
  | 'reset';

export interface PlazaStallChanged {
  readonly v: 1;
  readonly type: 'stall_changed';
  readonly instanceId: number;
  readonly stallId: string;
  readonly reason: PlazaStallChangeReason;
}

export interface PlazaSellerSale {
  readonly v: 1;
  readonly type: 'seller_sale';
  readonly instanceId: number;
  readonly stallId: string;
  readonly userId: string;
  readonly kind: 'pet' | 'eggs';
  readonly quantity: number;
  readonly netCrystals: number;
  readonly grossCrystals?: number;
  readonly commissionCrystals?: number;
  readonly saleId?: string;
  readonly buyerDisplayName?: string;
  readonly defId?: string;
  readonly variant?: string;
  readonly createdAt?: string;
}

/** Instancja PlayerState po stronie klienta (schema 2.x callbacki). */
interface PlayerStateClient {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarSkin: string;
  readonly isGuest: boolean;
  readonly x: number;
  readonly y: number;
  readonly dir: string;
  readonly equippedPets: ArrayLike<RemoteEquippedPetView>;
  onChange(cb: () => void): () => void;
}

/** Stan pokoju po stronie klienta — tylko to, czego używamy. */
interface PlazaStateClient {
  players: {
    onAdd(cb: (player: PlayerStateClient, sessionId: string) => void, triggerAll?: boolean): () => void;
    onRemove(cb: (player: PlayerStateClient, sessionId: string) => void): () => void;
  };
}

/** Pet na półce oferty (5.C.2). */
export interface TradeOfferPet {
  readonly uid: string;
  readonly defId: string;
  readonly variant: string;
}

/** Unlock achievementu przekazany w `trade_completed` (5.D.2d). */
export interface TradeAchievementUnlock {
  readonly id: string;
  readonly label: string;
  readonly crystals_reward: number;
}

/** Pozycja jajek na półce oferty (E-5) — rodzaj + ilość. */
export interface TradeOfferEgg {
  readonly defId: string;
  readonly quantity: number;
}

/** Oferta jednej strony (5.C.2 + 5.C.3 lock/confirm). */
export interface TradeOfferView {
  readonly pets: readonly TradeOfferPet[];
  readonly crystals: number;
  /** E-5: jajka po typie i ilości. */
  readonly eggs: readonly TradeOfferEgg[];
  /** 5.C.3: strona zablokowała ofertę (gotowa). */
  readonly locked: boolean;
  /** 5.C.3: strona potwierdziła (wymaga obu locked). */
  readonly confirmed: boolean;
}

/** Server-authoritative snapshot transientnego handlu po reconnect. */
export interface TradeResyncView {
  readonly tradeId: string | null;
  readonly phase?: 'invite' | 'pending' | 'active';
  readonly peerName?: string;
}

export interface PlazaHandlers {
  /** Nowy gracz (też istniejący przy wejściu — triggerAll). */
  onAdd: (sessionId: string, player: RemotePlayerView) => void;
  /** Gracz wyszedł / rozłączył się. */
  onRemove: (sessionId: string) => void;
  /** Zmiana pól gracza (głównie x/y/dir z `move`). */
  onChange: (sessionId: string, player: RemotePlayerView) => void;
  /** Zmiana equipped petów gracza w plazie (serwerowy snapshot z DB). */
  onEquippedPetsUpdated?: (sessionId: string, pets: RemoteEquippedPetView[]) => void;
  /** Przed re-wire po reconnect — scena czyści nieaktualne awatary. */
  onReset?: () => void;
  /** Token tej samej sesji wygasł; handel starej sesji już nie istnieje. */
  onSessionLost?: () => void;
  /** Po odzyskaniu WS: napraw dane, których event mógł zginąć w przerwie. */
  onRecovered?: (sessionResumed: boolean) => void;
  /** Zmiana statusu połączenia (UI: „łączę ponownie..." / off). */
  onStatus?: (status: PlazaStatus) => void;
  // ── Handel bezpośredni (5.C.1) ─────────────────────────────────────────────
  /** Ktoś zaprosił NAS do handlu → pokaż modal Tak/Nie. */
  onTradeInvite?: (tradeId: string, fromName: string) => void;
  /** Nasza propozycja wysłana → „czekam na akceptację {toName}". */
  onTradeProposed?: (tradeId: string, toName: string) => void;
  /** Handel rozpoczęty (zaakceptowany) → otwórz okno wymiany. */
  onTradeStarted?: (tradeId: string, peerName: string) => void;
  /** Naprawa modala/ofert po utracie wiadomości w przerwie połączenia. */
  onTradeResync?: (trade: TradeResyncView) => void;
  /** Handel zakończony/anulowany (declined/cancelled/peer_left/...). */
  onTradeCancelled?: (tradeId: string, reason: string) => void;
  /**
   * Wymiana sfinalizowana (5.C.4) — pety + 💎 przeniesione. Odśwież save.
   * `achievements` (5.D.2d) = unlocki TEJ strony (realtime przekazał per-stronę).
   */
  onTradeCompleted?: (tradeId: string, achievements: TradeAchievementUnlock[]) => void;
  /** Błąd propozycji (guest_cannot_trade / target_busy / ...). */
  onTradeError?: (reason: string) => void;
  /** Zmiana ofert (5.C.2) — po każdym add/remove/set_crystals którejkolwiek strony. */
  onTradeState?: (tradeId: string, myOffer: TradeOfferView, theirOffer: TradeOfferView) => void;
  /** Presetowy komunikat nad graczem (bez pełnego chatu). */
  onEmote?: (sessionId: string, presetId: PlazaCommsPresetId, petDefId: string | null, durationMs: number) => void;
  /** Błąd presetowego komunikatu (cooldown / guest trade). */
  onEmoteError?: (reason: string) => void;
  /** Commit zmiany budki — klient może natychmiast odświeżyć scenę/modal. */
  onStallChanged?: (event: PlazaStallChanged) => void;
  /** Sprzedawca online: sprzedaż + kwota netto do toastu i resyncu salda. */
  onSellerSale?: (event: PlazaSellerSale) => void;
  /** Błąd odczytu DB w handlerze realtime — UI może pokazać łagodny komunikat. */
  onRealtimeError?: (reason: string) => void;
}

export interface PlazaConnection {
  /** Nasz sessionId — zmienia się po reconnect (świeży join). */
  readonly sessionId: string;
  /** Wyślij swoją pozycję (serwer clampuje). No-op gdy chwilowo brak pokoju. */
  sendMove: (x: number, y: number, dir: string) => void;
  /** Zaproponuj handel graczowi o danym sessionId (5.C.1). */
  proposeTrade: (targetSessionId: string) => void;
  /** Zaakceptuj zaproszenie do handlu. */
  acceptTrade: (tradeId: string) => void;
  /** Odrzuć zaproszenie do handlu. */
  declineTrade: (tradeId: string) => void;
  /** Anuluj trwający handel. */
  cancelTrade: (tradeId: string) => void;
  /** Dodaj peta do mojej oferty (5.C.2). */
  addTradePet: (tradeId: string, petUid: string) => void;
  /** Zdejmij peta z mojej oferty. */
  removeTradePet: (tradeId: string, petUid: string) => void;
  /** Ustaw 💎 w mojej ofercie (serwer clampuje do salda). */
  setTradeCrystals: (tradeId: string, crystals: number) => void;
  /** Ustaw ilość jajek danego typu w ofercie (0 = usuń) — E-5. */
  setTradeEggs: (tradeId: string, defId: string, quantity: number) => void;
  /** Zablokuj moją ofertę (gotowa) — 5.C.3. */
  lockTrade: (tradeId: string) => void;
  /** Odblokuj moją ofertę (kasuje confirm obu stron) — 5.C.3. */
  unlockTrade: (tradeId: string) => void;
  /** Potwierdź wymianę (wymaga obu locked) — 5.C.3. */
  confirmTrade: (tradeId: string) => void;
  /** Odśwież mój snapshot equipped petów po equip/unequip w plazie. */
  refreshEquippedPets: () => void;
  /** Odśwież mój server-authoritative skin/nick po zmianie profilu. */
  refreshProfile: () => void;
  /** Wyślij presetowy komunikat nad postacią. */
  sendEmote: (presetId: PlazaCommsPresetId, petDefId?: string) => void;
  /** Rozłącz + posprzątaj listenery i zatrzymaj reconnect. Idempotent. */
  leave: () => void;
}

function equippedPetsSnapshot(p: PlayerStateClient): RemoteEquippedPetView[] {
  const pets: RemoteEquippedPetView[] = [];
  const raw = p.equippedPets;
  // Colyseus: pole ArraySchema bywa `undefined` zanim pierwszy sync je ustawi
  // (zwłaszcza przy `onAdd(..., true)` dla już obecnych graczy). Bez tego guardu
  // `raw.length` rzucał TypeError → wywalał onAdd/onChange → pokój się rozłączał
  // → PĘTLA reconnectów → emotki i inne wiadomości realtime nie wychodziły.
  // Brak/niezsynchronizowane pety = pusty snapshot (dosync uzupełni przy onChange).
  if (!raw) return pets;
  for (let i = 0; i < raw.length; i++) {
    const ep = raw[i];
    if (!ep) continue;
    pets.push({ uid: ep.uid, defId: ep.defId, variant: ep.variant });
  }
  return pets;
}

function snapshot(p: PlayerStateClient): RemotePlayerView {
  return {
    userId: p.userId,
    displayName: p.displayName,
    avatarSkin: p.avatarSkin,
    isGuest: p.isGuest,
    x: p.x,
    y: p.y,
    dir: p.dir,
    equippedPets: equippedPetsSnapshot(p),
  };
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Kod błędu Colyseus/HTTP bez uzależniania wywołującego od klasy biblioteki. */
export function plazaConnectionErrorCode(error: unknown): number | undefined {
  if (error && typeof error === 'object' && typeof (error as { code?: unknown }).code === 'number') {
    return (error as { code: number }).code;
  }
  return undefined;
}

/**
 * Dołącza do pokoju plazy i podpina callbacki stanu. Rzuca gdy PIERWSZY join
 * padnie (brak/zły cookie → 401, albo serwer niedostępny) — caller decyduje
 * (np. zostać w lokalnej scenie placu bez innych graczy). Po udanym pierwszym
 * połączeniu zerwania są obsługiwane wewnętrznie (auto-reconnect).
 */
export async function joinPlaza(
  handlers: PlazaHandlers,
  selfUserId: string,
): Promise<PlazaConnection> {
  const client = new Client(REALTIME_URL);

  let currentRoom: Room<PlazaStateClient> = await client.joinOrCreate<PlazaStateClient>(ROOM_NAME);
  let left = false;
  let reconnecting = false;
  // Deregistery aktualnego pokoju — czyścimy przed re-wire i przy leave.
  let roomCleanups: Array<() => void> = [];

  function wire(room: Room<PlazaStateClient>): void {
    const selfId = room.sessionId;
    const offChange = new Map<string, () => void>();

    // Pomijamy własną bieżącą sesję i ewentualną starą „ghost session" tego
    // samego usera, której serwerowe 30 s jeszcze nie zdążyło wygasnąć.
    const offAdd = room.state.players.onAdd((player, sessionId) => {
      if (sessionId === selfId || player.userId === selfUserId) return;
      handlers.onAdd(sessionId, snapshot(player));
      offChange.set(sessionId, player.onChange(() => handlers.onChange(sessionId, snapshot(player))));
    }, true);

    const offRemove = room.state.players.onRemove((_player, sessionId) => {
      if (sessionId === selfId || _player.userId === selfUserId) return;
      offChange.get(sessionId)?.();
      offChange.delete(sessionId);
      handlers.onRemove(sessionId);
    });

    room.onError((code, message) => {
      // eslint-disable-next-line no-console
      console.warn(`[plazaRoom] room error ${code}: ${message ?? ''}`);
    });

    room.onLeave((code) => {
      // Czyste zamknięcie z naszej strony / consented (kick/leave) → nie wznawiamy.
      if (left || NO_RECONNECT_CLOSE_CODES.has(code)) return;
      void reconnectLoop();
    });

    // ── Handel bezpośredni (5.C.1) — wiadomości serwera ──────────────────────
    room.onMessage('trade_invite', (m: { tradeId: string; fromName: string }) =>
      handlers.onTradeInvite?.(m.tradeId, m.fromName),
    );
    room.onMessage('trade_proposed', (m: { tradeId: string; toName: string }) =>
      handlers.onTradeProposed?.(m.tradeId, m.toName),
    );
    room.onMessage('trade_started', (m: { tradeId: string; peerName: string }) =>
      handlers.onTradeStarted?.(m.tradeId, m.peerName),
    );
    room.onMessage('trade_resync', (m: TradeResyncView) => handlers.onTradeResync?.(m));
    room.onMessage('trade_cancelled', (m: { tradeId: string; reason: string }) =>
      handlers.onTradeCancelled?.(m.tradeId, m.reason),
    );
    room.onMessage('trade_completed', (m: { tradeId: string; achievements?: TradeAchievementUnlock[] }) =>
      handlers.onTradeCompleted?.(m.tradeId, m.achievements ?? []),
    );
    room.onMessage('trade_error', (m: { reason: string }) => handlers.onTradeError?.(m.reason));
    room.onMessage('trade_state', (m: { tradeId: string; myOffer: TradeOfferView; theirOffer: TradeOfferView }) =>
      handlers.onTradeState?.(m.tradeId, m.myOffer, m.theirOffer),
    );
    room.onMessage('equipped_pets_updated', (m: { sessionId: string; pets: RemoteEquippedPetView[] }) =>
      handlers.onEquippedPetsUpdated?.(m.sessionId, m.pets ?? []),
    );
    room.onMessage(
      'emote_show',
      (m: { sessionId: string; presetId: PlazaCommsPresetId; petDefId?: string | null; durationMs?: number }) =>
        handlers.onEmote?.(m.sessionId, m.presetId, m.petDefId ?? null, m.durationMs ?? 3600),
    );
    room.onMessage('emote_error', (m: { reason: string }) => handlers.onEmoteError?.(m.reason));
    room.onMessage('plaza_stall_changed', (m: PlazaStallChanged) => handlers.onStallChanged?.(m));
    room.onMessage('plaza_seller_sale', (m: PlazaSellerSale) => handlers.onSellerSale?.(m));
    room.onMessage('realtime_error', (m: { reason?: string }) =>
      handlers.onRealtimeError?.(m.reason ?? 'service_unavailable'),
    );

    roomCleanups = [
      offAdd,
      offRemove,
      () => {
        for (const off of offChange.values()) off();
        offChange.clear();
      },
    ];
  }

  function clearRoomListeners(): void {
    for (const off of roomCleanups) off();
    roomCleanups = [];
  }

  async function reconnectLoop(): Promise<void> {
    if (left || reconnecting) return;
    reconnecting = true;
    handlers.onStatus?.('reconnecting');
    // Stare awatary są nieaktualne (sesje zniknęły) — niech scena je wyczyści.
    clearRoomListeners();
    handlers.onReset?.();
    const reconnectStartedAt = Date.now();
    const reconnectionToken = currentRoom.reconnectionToken;
    let sessionLostNotified = false;
    let resumeImpossible = false;

    for (let attempt = 1; !left; attempt++) {
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** Math.min(attempt - 1, 4), RECONNECT_CAP_MS);
      await sleep(delay);
      if (left) break;
      const canResume = !resumeImpossible
        && Boolean(reconnectionToken)
        && Date.now() - reconnectStartedAt < SESSION_RESUME_WINDOW_MS;
      try {
        if (canResume) {
          currentRoom = await client.reconnect<PlazaStateClient>(reconnectionToken);
        } else {
          if (!sessionLostNotified) {
            sessionLostNotified = true;
            handlers.onSessionLost?.();
          }
          currentRoom = await client.joinOrCreate<PlazaStateClient>(ROOM_NAME);
        }
        if (left) {
          void currentRoom.leave();
          break;
        }
        wire(currentRoom);
        // Handler `trade_resync` jest już podpięty. Serwer wysyła snapshot także
        // sam po allowReconnection, ale ta prośba usuwa wyścig handshake/wire.
        if (canResume) currentRoom.send('trade_resync', {});
        reconnecting = false;
        handlers.onStatus?.('connected');
        handlers.onRecovered?.(canResume);
        // eslint-disable-next-line no-console
        console.info(`[plazaRoom] reconnected (session=${currentRoom.sessionId})`);
        return;
      } catch (err) {
        const code = plazaConnectionErrorCode(err);
        if (canResume && RESUME_IMPOSSIBLE_ERROR_CODES.has(code ?? -1)) {
          resumeImpossible = true;
          if (!sessionLostNotified) {
            sessionLostNotified = true;
            handlers.onSessionLost?.();
          }
          // Pokój z poprzedniego procesu już nie istnieje. Następna próba robi
          // fresh join bez czekania do końca 30-sekundowego okna tokenu.
          continue;
        }
        if (code === 401 || code === 4215) {
          if (!sessionLostNotified) handlers.onSessionLost?.();
          left = true;
          reconnecting = false;
          handlers.onStatus?.('disconnected');
          return;
        }
        // eslint-disable-next-line no-console
        console.warn(`[plazaRoom] reconnect próba ${attempt} nieudana`, err);
        // Po kilku próbach pokaż twardszy status, ale nadal naprawiaj w tle.
        if (attempt === 6) handlers.onStatus?.('disconnected');
      }
    }

    reconnecting = false;
    if (!left) handlers.onStatus?.('disconnected');
  }

  wire(currentRoom);
  handlers.onStatus?.('connected');

  // Bezpieczna wysyłka — no-op gdy rozłączeni/w trakcie reconnectu/pokój padł.
  const safeSend = (type: string, payload: unknown): void => {
    if (left || reconnecting) return;
    try {
      currentRoom.send(type, payload);
    } catch {
      // Pokój mógł paść między klatkami — reconnectLoop to obsłuży.
    }
  };

  return {
    get sessionId(): string {
      return currentRoom.sessionId;
    },
    sendMove: (x, y, dir) => safeSend('move', { x, y, dir }),
    proposeTrade: (targetSessionId) => safeSend('trade_propose', { targetSessionId }),
    acceptTrade: (tradeId) => safeSend('trade_accept', { tradeId }),
    declineTrade: (tradeId) => safeSend('trade_decline', { tradeId }),
    cancelTrade: (tradeId) => safeSend('trade_cancel', { tradeId }),
    addTradePet: (tradeId, petUid) => safeSend('trade_add_pet', { tradeId, petUid }),
    removeTradePet: (tradeId, petUid) => safeSend('trade_remove_pet', { tradeId, petUid }),
    setTradeCrystals: (tradeId, crystals) => safeSend('trade_set_crystals', { tradeId, crystals }),
    setTradeEggs: (tradeId, defId, quantity) => safeSend('trade_set_eggs', { tradeId, defId, quantity }),
    lockTrade: (tradeId) => safeSend('trade_lock', { tradeId }),
    unlockTrade: (tradeId) => safeSend('trade_unlock', { tradeId }),
    confirmTrade: (tradeId) => safeSend('trade_confirm', { tradeId }),
    refreshEquippedPets: () => safeSend('refresh_equipped_pets', {}),
    refreshProfile: () => safeSend('refresh_profile', {}),
    sendEmote: (presetId, petDefId) => safeSend('emote_send', { presetId, petDefId }),
    leave: () => {
      if (left) return;
      left = true;
      clearRoomListeners();
      void currentRoom.leave();
    },
  };
}
